/**
 * A porta entre a tela e as rotas de sessão.
 *
 * Ela é pequena de propósito: entrar, sair, e perguntar quem sou eu. O token
 * não aparece em lugar nenhum deste arquivo — ele viaja num cookie `HttpOnly`,
 * que é exatamente o cookie que nenhum JavaScript desta página alcança. O que
 * o `fetch` faz é pedir as credenciais (`@/lib/api`), e o navegador cuida do
 * resto.
 */

import type { ContaDeSuporte } from './conta-de-suporte';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';

/**
 * A base e as credenciais vem de `@/lib/api`, num lugar so.
 *
 * Vazio quer dizer `/api`, que o proxy do Vite reescreve em
 * desenvolvimento; num container ela e o endereco completo da API, assado
 * no build por `VITE_API_URL`. Nenhum nome de maquina mora neste arquivo.
 */
const BASE = BASE_DA_API;

/** Não há sessão aberta. É a resposta de quem ainda não entrou. */
export class SemSessao extends Error {
  constructor() {
    super('não há sessão aberta');
    this.name = 'SemSessao';
  }
}

/** O backend não respondeu: rede, porta fechada, processo fora do ar. */
export class ApiIndisponivel extends Error {
  constructor() {
    super('o sistema não respondeu');
    this.name = 'ApiIndisponivel';
  }
}

/** O pedido chegou e foi recusado — com o que fazer em seguida, do servidor. */
export class RecusaDaApi extends Error {
  readonly comoResolver: string;
  readonly status: number;

  constructor(erro: string, comoResolver: string, status: number) {
    super(erro);
    this.name = 'RecusaDaApi';
    this.comoResolver = comoResolver;
    this.status = status;
  }
}

interface CorpoDeErro {
  readonly erro?: string;
  readonly comoResolver?: string;
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      credentials: CREDENCIAIS_DA_API,
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiIndisponivel();
  }

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
    /* O 401 de `/sessao/atual` é "ainda não entrei", e não uma recusa a
       corrigir. Ele vira `SemSessao` para a casca poder mandar a pessoa ao
       login em vez de desenhar uma mensagem de erro. O 401 de `POST /sessao`
       é outra coisa — é a senha que não confere —, e por isso a entrada o
       trata antes de chegar aqui. */
    if (resposta.status === 401) throw new SemSessao();
    throw new RecusaDaApi(
      corpo.erro ?? 'o pedido não foi aceito',
      corpo.comoResolver ?? 'Tente outra vez; se continuar, avise o suporte com o horário.',
      resposta.status,
    );
  }

  return (await resposta.json()) as T;
}

export interface PapelDaSessao {
  readonly codigo: string;
  readonly nome: string;
  /** O escopo por filial (0081): o nome da filial, ou nulo para a empresa inteira. */
  readonly filial?: string | null;
}

/**
 * 0128 · A TRAVA DO LANÇAMENTO DE ABASTECIMENTO da pessoa: a união dos papéis
 * dela que lançam. `null`, dentro dela, é "sem trava daquilo". Os tanques vêm
 * com o código e o nome, como o cadastro os grava.
 */
export interface TravaDoLancamento {
  readonly tipos: readonly ('interno' | 'externo')[] | null;
  readonly tanques: readonly { readonly id: string; readonly codigo: string; readonly nome: string }[] | null;
}

export interface SessaoDaApi {
  readonly usuarioId: string;
  readonly nome: string;
  readonly email: string | null;
  readonly empresa: string;
  readonly papeis: readonly PapelDaSessao[];
  /**
   * O que esta pessoa pode fazer, pelas linhas de `org.papel_permissao`.
   *
   * E a MESMA fonte que o portao do servidor confere. A navegacao lateral
   * passou a se recortar por ela em vez de pela proposta: proposta e derivacao
   * da secao 1.4 do guia, permissao e o que o banco tem, e as duas divergem na
   * primeira concessao alterada pela tela de governanca.
   */
  readonly permissoes: readonly string[];
  /** Só na sessão de suporte da plataforma (0033): só leitura, e cada tela na trilha da empresa. */
  readonly suporte?: boolean;
  /**
   * O acesso cobre só parte da empresa (uma filial, uma obra ou um contrato):
   * o servidor recusa o que é da empresa inteira — orçamento, renovação — e a
   * tela não oferece (`acesso/alcance.ts`).
   */
  readonly alcanceParcial?: boolean;
  /**
   * 0128 · Algum papel da pessoa trava o lançamento de abastecimento: o tipo,
   * o tanque, ou os dois. Ausente quando nada está travado — o comportamento
   * de antes. A tela trava o campo e não oferece o que o servidor recusaria.
   */
  readonly travaDoLancamento?: TravaDoLancamento;
  /**
   * 0130 · O início do app que os papéis da pessoa escolheram (decisão 55).
   * Ausente é o painel de sempre.
   */
  readonly inicioDoApp?: InicioDoApp;
  /**
   * 0132 · A sessão é de um POSTO PARCEIRO (decisão 58): gente de fora da
   * empresa, que só lança o abastecimento no posto do fornecedor dela. O
   * servidor recusa toda rota fora do lançamento; a tela só abre ele.
   */
  readonly parceiro?: ParceiroDaSessao;
  /** 0134 · Quem também administra a plataforma com o mesmo e-mail: o vínculo e a senha unificada. */
  readonly contaDeSuporte?: ContaDeSuporte;
}

/** O fornecedor do posto parceiro da sessão (0132). */
export interface ParceiroDaSessao {
  readonly fornecedorId: string;
  readonly fornecedor: string;
}

/** Os inícios do app que um papel escolhe (0130). `padrao` é o painel (M04). */
export type InicioDoApp = 'padrao' | 'abastecimento';

/** O desfecho de `entrar`: entrou, ou falta o codigo do segundo fator (0085). */
export type ResultadoDaEntrada = { readonly entrou: true } | { readonly entrou: false; readonly pendente: string };

export const apiDeSessao = {
  atual: () => pedir<SessaoDaApi>('/sessao/atual'),

  /**
   * Entrar. O 401 daqui NÃO é `SemSessao`: é a senha que não confere, e a
   * pessoa tem o que fazer a respeito. Por isso esta chamada não passa pelo
   * `pedir` acima — ela lê a resposta antes de decidir o que lançar.
   */
  entrar: async (email: string, senha: string, manterConectado?: boolean): Promise<ResultadoDaEntrada> => {
    let resposta: Response;
    try {
      resposta = await fetch(`${BASE}/sessao`, {
        method: 'POST',
        credentials: CREDENCIAIS_DA_API,
        headers: { 'content-type': 'application/json' },
        /* `manterConectado` so vai quando o app do celular o informa (M01); a mesa
           nao manda, e o servidor mantem o prazo de sempre. */
        body: JSON.stringify({ email, senha, ...(manterConectado === undefined ? {} : { manterConectado }) }),
      });
    } catch {
      throw new ApiIndisponivel();
    }
    if (resposta.ok) {
      /* O SEGUNDO FATOR (0085): a senha conferiu e falta o codigo. A sessao
         ainda nao existe; o que volta e o identificador da etapa do codigo. */
      const corpo = (await resposta.json().catch(() => ({}))) as { segundoFator?: boolean; pendente?: string };
      return corpo.segundoFator === true && typeof corpo.pendente === 'string'
        ? { entrou: false, pendente: corpo.pendente }
        : { entrou: true };
    }

    const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
    throw new RecusaDaApi(
      corpo.erro ?? 'não foi possível entrar',
      corpo.comoResolver ?? 'Confira o e-mail e a senha e tente outra vez.',
      resposta.status,
    );
  },

  /**
   * A etapa do codigo (0085). Como `entrar`, le a resposta antes de decidir:
   * o 401 daqui e "a etapa venceu", e a saida e a senha de novo — nao a casca
   * mandando para o login.
   */
  confirmarCodigo: async (pendente: string, codigo: string, manterConectado?: boolean): Promise<{ readonly usouRecuperacao: boolean }> => {
    let resposta: Response;
    try {
      resposta = await fetch(`${BASE}/sessao/segundo-fator`, {
        method: 'POST',
        credentials: CREDENCIAIS_DA_API,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pendente, codigo, ...(manterConectado === undefined ? {} : { manterConectado }) }),
      });
    } catch {
      throw new ApiIndisponivel();
    }
    if (resposta.ok) {
      const corpo = (await resposta.json().catch(() => ({}))) as { usouRecuperacao?: boolean };
      return { usouRecuperacao: corpo.usouRecuperacao === true };
    }
    const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
    throw new RecusaDaApi(
      corpo.erro ?? 'o código não foi aceito',
      corpo.comoResolver ?? 'Digite o código que o aplicativo autenticador mostra agora.',
      resposta.status,
    );
  },

  sair: () => pedir<{ saiu: boolean }>('/sessao', { method: 'DELETE' }),
} as const;
